import { ethSignAesDecrypt } from '@ethsign/utils-web';
import { uniqBy } from 'lodash-es';

import { getShareInfo, getShareRecipients } from '@/services';
import { ContractItem, ContractType } from '@/types';
import { addAnnotationToPDF, convertXmlToJSON, download, downloadContractFile, getReadOnlyTextXml } from '@/utils';
import { ControllerBase } from '@/utils/bizify';

export type PageData = {
  contract: ContractItem;
  file: any;
  modalOpen: boolean;
  loading: boolean;
  encryptFile: string;
  password: string;
  id: string;
  xfdfStrings: string[] | any;
  subContractXfdfStrings: string[] | any;
  subContract: ContractItem;
  recipientsPage: number;
  recipientsHasMore?: boolean;
};

export class PageCtrl extends ControllerBase<PageData> {
  protected $data(): any {
    return {
      contract: null,
      file: null,
      modalOpen: false,
      permissionModalOpen: false,
      loading: false,
      encryptFile: '',
      password: '',
      id: '',
      xfdfStrings: [],
      subContractXfdfStrings: [],
      subContract: null,
      recipientsPage: 1,
      recipientsHasMore: false
    };
  }
  initInfo = (data: { id: string; isVisualContractView: boolean }): void => {
    const { id } = data;
    this.state.id = id;
    this.getData(id);
  };
  getData = async (id: string): Promise<void> => {
    this.state.loading = true;
    try {
      const [contract] = await Promise.all([
        getShareInfo({
          id
        })
      ]);
      this.state.contract = contract;

      if (
        (contract.contractType === ContractType.visual_contract && contract.clientStatus !== 'wait_me') ||
        contract.contractType === ContractType.link_sign_contract
      ) {
        await this.getRecipients(id);
      }
      this.state.encryptFile = contract.fileContent;

      if (contract.enableEncryption) {
        this.state.loading = false;
        this.state.modalOpen = true;
      } else {
        const fileContent = contract.fileContent && JSON.parse(contract.fileContent);
        fileContent.annotation = await convertXmlToJSON(fileContent.annotation);
        this.state.file = fileContent;
        this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients);
      }
    } catch (error) {
      console.log(error);
    }
  };
  getXfdfString = async (recipients: any[], password?: string): Promise<any[]> => {
    const signedRecipients = recipients.filter((item: any) => item.role === 'signer' && item.status === 'signed');
    if (password) {
      return await Promise.all(
        signedRecipients.map(async (item: any) => {
          let res = item.signingContent
            ? await ethSignAesDecrypt(JSON.parse(item.signingContent)?.signingContent, password)
            : '';
          res = await convertXmlToJSON(res);
          return res;
        })
      );
    } else {
      return await Promise.all(
        signedRecipients.map((item: any) =>
          convertXmlToJSON(item.signingContent ? JSON.parse(item.signingContent).signingContent : '')
        )
      );
    }
  };
  getRecipients = async (id: string): Promise<void> => {
    try {
      this.state.loading = true;
      const recipients = await getShareRecipients(id, this.state.recipientsPage);
      if (recipients) {
        this.state.recipientsPage += 1;
        this.state.contract.recipients = uniqBy(
          [
            ...(recipients?.rows || []).filter((item: any) => item.role === 'signer'),
            ...this.state.contract.recipients.filter((item: any) => item.role === 'signer')
          ],
          'address'
        );
        this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients, this.state.password);
        this.state.contract.recipientsTotal = recipients?.total || 0;
      }
      if (recipients?.total <= this.state.contract.recipients?.length) {
        this.state.recipientsHasMore = false;
      } else {
        this.state.recipientsHasMore = true;
      }
      this.state.loading = false;
    } catch (error) {
      this.state.loading = false;
      console.log(error);
    }
  };
  getContract = async (id: string, isNotModify?: boolean): Promise<any> => {
    if (isNotModify) {
      const res = {
        ...this.state.contract,
        id,
        recipients: [this.state.contract.recipients.find((item) => item.contractId === id)]
      };
      return res;
    }
    const subRecipients = [this.state.contract.recipients.find((item) => item.contractId === id)];
    this.state.subContract = { ...this.state.contract, id, recipients: subRecipients } as ContractItem;
    this.state.subContractXfdfStrings = await this.getXfdfString(subRecipients, this.state.password);
  };
  passwordConfirm = async (file: { fileString: string; annotation: string }, password: string): Promise<void> => {
    file.annotation = await convertXmlToJSON(file.annotation);
    this.state.file = file;
    this.state.password = password;
    this.state.modalOpen = false;
    this.state.loading = true;
    this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients, password);
    this.state.subContractXfdfStrings =
      this.state.subContract && (await this.getXfdfString(this.state.subContract.recipients, this.state.password));
  };
  setLoading = (loading: boolean): void => {
    this.state.loading = loading;
  };
  setModalOpen = (open: boolean): void => {
    if (!open) {
      if (this.state.file) {
        this.state.modalOpen = false;
      }
    } else {
      this.state.modalOpen = open;
    }
  };

  downloadFile = async (id?: string): Promise<void> => {
    this.state.loading = true;
    try {
      if (!id) {
        const { recipients, contractType } = this.state.contract;
        const fileContent = this.state.encryptFile;
        await downloadContractFile({
          recipients,
          contractType,
          fileContent,
          password: this.state.password,
          data: {
            id: this.state.id,
            enableEncryption: this.state.contract.enableEncryption,
            title: this.state.contract.title
          }
        });
      } else {
        const contractType = this.state.contract.contractType;
        let contract;
        let xfdfStrings;
        if (contractType === ContractType.visual_contract) {
          if (this.state.subContract && id === this.state.subContract.id) {
            contract = this.state.subContract;
            xfdfStrings = [...this.state.subContractXfdfStrings];
          } else {
            contract = await this.getContract(id, true);
            xfdfStrings = await this.getXfdfString(contract.recipients, this.state.password);
          }
        } else {
          contract = this.state.contract;
          xfdfStrings = [...this.state.xfdfStrings];
          const annotationXml = await getReadOnlyTextXml(this.state.file.annotation);
          annotationXml && xfdfStrings.push(annotationXml);
        }
        const documentBuffer = await addAnnotationToPDF(this.state.file.fileString, xfdfStrings, {
          id: contract.id,
          enableEncryption: contract.enableEncryption,
          version: 1
        });
        download(documentBuffer, contract.title + '.pdf', 'application/pdf');
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.state.loading = false;
    }
  };
}
